import { Grid } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { FormProvider, useForm } from 'react-hook-form';
import { createInvoiceArticle } from 'src/shared/services/invoiceArticleService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { ControlledAutocompleteDropdown } from 'src/shared/components/form/controlledAutocompleteDropdown';
import { getAllInvoiceArticleCategory } from 'src/shared/services/invoiceArticleCategoryService';
import { VatRateEnum } from 'src/shared/models/enums/vatRate.enum';
import { InvoiceArticleRequest } from '../models/invoiceArticleRequest';
import { InvoiceArticleCategory } from '../models/invoiceArticleCategory';
import { InvoiceArticleCategorySearchFilter } from '../models/invoiceArticleCategorySearchFilter';
import { VatRateSelector } from './vatRateSelector';

type CreateInvoiceArticleProps = {
  setOpenCreateArticle: (openCreateArticle: boolean) => void;
  setRefetchDataGrid: (refetch: boolean) => void;
};

export const CreateInvoiceArticle = (props: CreateInvoiceArticleProps) => {
  const { setOpenCreateArticle, setRefetchDataGrid } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState<boolean>(false);
  const [categories, setCategories] = useState<InvoiceArticleCategory[]>();

  const minimumCharRequired = 3;
  const maximumCharRequired = 20;
  const minimumArticleNr = 100;
  const minCharRequiredMessage = t('common:validationMessages.minLength', { count: minimumCharRequired });
  const maxCharRequiredMessage = t('common:validationMessages.maxLength', { count: maximumCharRequired });
  const minimumArticleNrMessage = t('invoice:articleRegister.articleNrMin', { value: minimumArticleNr });

  const filter = useMemo<InvoiceArticleCategorySearchFilter>(
    () => ({
      pageIndex: 0,
      pageSize: 1000,
      sortCol: 'articleCategory',
      sortDir: 'asc',
      filter: '',
    }),
    [],
  );

  const schema = yup
    .object({
      name: yup.string().min(minimumCharRequired, minCharRequiredMessage).max(maximumCharRequired, maxCharRequiredMessage).required(),
      articleNr: yup.number().min(minimumArticleNr, t(minimumArticleNrMessage)).required(t('common:validationMessages.required')),
      price: yup
        .number()
        .typeError(t('common:validationMessages.amount'))
        .min(0, t('common:validationMessages.nonNegative'))
        .required(t('common:validationMessages.required')),
      articleCategory_id: yup.number().optional(),
      vatRate: yup.string().required(t('common:validationMessages.required')),
    })
    .required();

  const formMethods = useForm<InvoiceArticleRequest>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      articleNr: '0',
      price: 0,
      articleCategory_id: 0,
      vatRate: VatRateEnum.Zero,
    },
  });
  const { handleSubmit, setValue, watch } = formMethods;
  const handleClose = () => {
    setOpenCreateArticle(false);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAllInvoiceArticleCategory(filter);
      if (isMounted()) {
        const items = result.data.results;
        setCategories(items);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [filter, isMounted]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const onSubmit = async (data: InvoiceArticleRequest) => {
    setLoading(true);

    const dataToPost: InvoiceArticleRequest = {
      ...data,
      vatRate: data.price > 0 ? data.vatRate : VatRateEnum.Zero,
    };

    try {
      await createInvoiceArticle(dataToPost);
      if (isMounted()) {
        toast.success(<>{t('invoice:articleRegister.createSuccess')}</>);
        handleClose();
        setRefetchDataGrid(true);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.error(<>{t('invoice:articleRegister.error')}</>);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <GenericDialog
          maxWidth='sm'
          open
          onClose={handleClose}
          onSave={handleSubmit(onSubmit)}
          dialogTitle={t('invoice:articleRegister.createArticle')}
          dialogContent={
            <Grid container spacing={2}>
              <LoadingOverlay loading={loading} />
              <Grid item xs={12} sm={6}>
                <ControlledTextField label={t('invoice:articleRegister.articleName')} name='name' />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ControlledTextField type='number' label={t('invoice:articleRegister.articleNumber')} name='articleNr' />
              </Grid>
              <Grid item xs={6} sm={3}>
                <ControlledTextField label={t('invoice:articleRegister.price')} type='number' name='price' />
              </Grid>
              <Grid item xs={6} sm={3}>
                <VatRateSelector name='vatRate' disable={watch('price') < 0} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ControlledAutocompleteDropdown
                  disable={!categories || categories.length === 0}
                  componentWidth='100%'
                  nameProp='articleCategory'
                  label={t('invoice:articleRegister.category')}
                  placeholder={categories?.length === 0 ? t('invoice:articleRegister.noCategories') : t('common:button.choose')}
                  name='articleCategory_id'
                  options={categories || []}
                  callback={(selectedOption) => {
                    if (selectedOption) {
                      setValue('articleCategory_id', selectedOption.articleCategory_id);
                    } else {
                      setValue('articleCategory_id', 0);
                    }
                  }}
                />
              </Grid>
            </Grid>
          }
        />
      </form>
    </FormProvider>
  );
};
